import { useState, type FormEvent } from 'react';
import { useApp } from '../state/store';
import { canAddTasks, canReview, getSession } from '../services/auth';
import { back, navigate } from '../lib/router';
import { C, HEALTH_COLORS, R, STAGES, font } from '../theme/tokens';
import { nextAction, progress, roleOf, tasksOf } from '../state/selectors';
import { ago, dayLabel, diffDays, whenLabel, userTimeZone, ymdIn } from '../lib/dates';
import { useNow } from '../lib/useNow';
import { Avatar, BackPill, Btn, Card, CardTitle, DarkCard, EmptyState, Eyebrow, FieldLabel, Mono, Photo, ProgressBar, Tag, inputStyle, safeTop, textareaStyle } from '../components/ui';
import type { Activity, DemoData } from '../domain/types';
import { TaskRow } from '../components/TaskRow';
import { seriesLabel } from '../domain/series';

export function ActivityScreen({ id }: { id: string }) {
  const { state: { data }, act } = useApp();
  const nowMs = useNow();
  const a = data.activities[id];
  if (!a) return <EmptyState title="Activity not found." body="It may have been removed or you may not have access." action={<Btn variant="primary" onClick={() => navigate({ name: 'home' })}>Back to Home</Btn>} />;

  const me = data.ambassadors[data.me];
  const today = ymdIn(nowMs, me.timeZone);
  const role = roleOf(data, a.id);
  const assigned = role !== null;
  const p = progress(data, a.id);
  const next = nextAction(data, a.id);
  const org = data.ambassadors[a.organiserId];
  const days = diffDays(today, ymdIn(a.startsAt, a.timeZone));
  const pill = a.stage === 4 ? 'COMPLETED' : a.stage === 0 ? 'PROPOSED' : days < 0 ? 'PAST' : days === 0 ? 'TODAY' : `IN ${days} DAY${days > 1 ? 'S' : ''}`;
  const updates = data.updates.filter((u) => u.activityId === a.id && (u.visibility === 'network' || assigned));
  const followed = Boolean(data.followedActivities[a.id]);
  const offered = Boolean(data.offeredHelp[a.id]);
  const share = () => (a.shareKitId ? act.openSheet({ kind: 'quickShare', kitId: a.shareKitId }) : act.toast('No approved share kit for this activity yet'));
  const hc = HEALTH_COLORS[a.health];

  return (
    <div className="slide-in">
      <div style={{ position: 'relative', height: 330, background: C.night }}>
        <div style={{ position: 'absolute', inset: 0 }}><Photo id={a.photo} eager pad={60} fallback={a.series} style={{ paddingBottom: a.photo?.startsWith('coin') ? 110 : undefined }} /></div>
        <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(180deg,rgba(11,11,15,.55) 0%,rgba(11,11,15,0) 30%,rgba(11,11,15,.92) 100%)' }} />
        <div style={{ position: 'absolute', top: safeTop(10), left: 16, right: 16, display: 'flex', justifyContent: 'space-between' }}>
          <BackPill onPhoto onClick={back} />
          <span style={{ height: 40, padding: '0 14px', borderRadius: R.pill, background: C.violet, color: C.white, font: font(600, 11, 1, 'mono'), display: 'flex', alignItems: 'center' }}>{pill}</span>
        </div>
        <div style={{ position: 'absolute', left: 20, right: 20, bottom: 18, color: C.white, display: 'flex', flexDirection: 'column', gap: 8 }}>
          <Eyebrow color={C.white}>{`${a.type} · ${seriesLabel(data.series, a.series)}`}</Eyebrow>
          <h1 style={{ margin: 0, font: font(700, 40, 0.92), letterSpacing: '-.045em' }}>{a.name}</h1>
          <Mono color={C.metal}>{`${dayLabel(ymdIn(a.startsAt, a.timeZone))} · ${whenLabel(a, userTimeZone())} · ${a.place}`.toUpperCase()}</Mono>
        </div>
      </div>

      <ol aria-label="Lifecycle" style={{ listStyle: 'none', margin: 0, padding: '16px 16px 0', display: 'flex', gap: 4 }}>
        {STAGES.map((s, i) => (
          <li key={s} aria-current={i === a.stage ? 'step' : undefined} style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 6 }}>
            <div style={{ height: 6, borderRadius: 6, background: i < a.stage ? C.ink : i === a.stage ? C.violet : C.subtle }} />
            <span style={{ font: font(600, 10), color: i === a.stage ? C.violet : i < a.stage ? C.ink : C.grey }}>{s}</span>
          </li>
        ))}
      </ol>

      {assigned ? (
        <DarkCard style={{ margin: '16px 12px 0' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Eyebrow color={C.rim} style={{ fontSize: '0.5625rem' }}>Your role</Eyebrow>
            <span style={{ font: font(700, 10), letterSpacing: '.12em', color: hc[1], display: 'flex', alignItems: 'center', gap: 6 }}>
              <span style={{ width: 7, height: 7, borderRadius: '50%', background: hc[1] }} />{a.health.toUpperCase()}
            </span>
          </div>
          <span style={{ font: font(700, 20, 1.15), letterSpacing: '-.02em' }}>{`${role} · ${a.organiserId === data.me ? me.title : `with ${org.name}`}`}</span>
          {a.brief && <span style={{ font: font(400, 13, 1.45), color: C.mutedDark }}>{a.brief}</span>}
          <div style={{ background: C.dark2, borderRadius: R.panel, padding: 14, display: 'flex', flexDirection: 'column', gap: 4 }}>
            <Eyebrow color={C.rim} style={{ fontSize: '0.5625rem' }}>Your next action</Eyebrow>
            <span style={{ font: font(700, 17, 1.2) }}>{next ? `${next.title}.` : a.stage === 0 ? 'Wait for approval, then build the team.' : 'Your tasks are done. Post an update for the team.'}</span>
            <Mono color={C.mutedDark}>{next ? `${next.due === today ? 'DUE TODAY · ' : 'DUE '}${dayLabel(next.due).toUpperCase()}` : a.stage === 0 ? 'PROPOSAL SENT' : `TEAM HAS ${p.total - p.done} OPEN`}</Mono>
          </div>
          {p.total > 0 && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between' }}><Mono color={C.mutedDark}>PROGRESS</Mono><Mono color={C.white}>{`${p.done} / ${p.total} tasks`}</Mono></div>
              <ProgressBar ratio={p.ratio} dark label="Activity progress" />
            </div>
          )}
          <Btn variant="primary" size="lg" full onClick={() => act.openSheet({ kind: 'update', activityId: a.id })}>Update progress</Btn>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0,1fr))', gap: 6 }}>
            <Btn variant="ghostDark" style={{ padding: 0 }} onClick={() => navigate({ name: 'collab', id: a.id })}>Collaborate</Btn>
            <Btn variant="ghostDark" style={{ padding: 0 }} onClick={() => act.openSheet({ kind: 'help', activityId: a.id })}>Get help</Btn>
            <Btn variant="ghostDark" style={{ padding: 0 }} onClick={share}>Share ↗</Btn>
          </div>
        </DarkCard>
      ) : (
        <DarkCard style={{ margin: '16px 12px 0', gap: 14 }}>
          <Eyebrow color={C.rim} style={{ fontSize: '0.5625rem' }}>Accountable organiser</Eyebrow>
          <button type="button" onClick={() => navigate({ name: 'profile', id: org.id })} style={{ display: 'grid', gridTemplateColumns: '48px 1fr auto', gap: 12, alignItems: 'center', border: 0, background: 'transparent', padding: 0, textAlign: 'left', color: C.white }}>
            <Avatar person={org} size={48} />
            <span style={{ display: 'flex', flexDirection: 'column', gap: 2 }}><span style={{ font: font(700, 17) }}>{org.name}</span><span style={{ font: font(500, 11), color: C.mutedDark }}>{org.title}</span></span>
            <span style={{ font: font(600, 13), color: C.rim }}>Profile ›</span>
          </button>
          <span style={{ font: font(400, 13, 1.45), color: C.mutedDark }}>{`Latest: ${updates[0]?.text ?? 'No updates yet.'}`}</span>
          <Btn variant={followed ? 'mark' : 'white'} size="lg" full aria-pressed={followed} onClick={() => act.toggleFollowActivity(a.id)}>{followed ? 'Following ✓' : 'Follow activity'}</Btn>
          {/* No Message action: direct messages are not built yet. */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0,1fr))', gap: 6 }}>
            <Btn variant="ghostDark" style={{ padding: 0, background: offered ? C.dark3 : 'transparent', borderColor: offered ? C.mark : C.darkBorder }} aria-pressed={offered} onClick={() => act.toggleOffer(a.id)}>{offered ? 'Offered ✓' : 'Offer help'}</Btn>
            <Btn variant="ghostDark" style={{ padding: 0 }} onClick={share}>Share ↗</Btn>
          </div>
        </DarkCard>
      )}

      {canReview(getSession(), a) && <ReviewPanel activity={a} />}
      {a.review === 'rejected' && (
        <div role="status" style={{ margin: '14px 12px 0', padding: 16, borderRadius: R.pulse, background: C.divider, font: font(500, 13, 1.45), color: C.ink }}>
          {`Not approved by UNIT Marketing & Events${a.decisionReason ? `: ${a.decisionReason}` : '.'}`}
        </div>
      )}

      {assigned && (p.total > 0 || canAddTasks(getSession(), a)) && (
        <Card style={{ padding: '18px 18px 6px' }}>
          <CardTitle right={<Mono size={11} weight={600} color={C.violet}>{`${p.done} / ${p.total} tasks`}</Mono>}>Tasks</CardTitle>
          <div style={{ marginTop: 4 }}>{tasksOf(data, a.id).map((t) => <TaskRow key={t.id} task={t} today={today} showOwner size={32} />)}</div>
          {!p.total && <p style={{ font: font(400, 13), color: C.grey, margin: '8px 0 12px' }}>No tasks yet.</p>}
          {canAddTasks(getSession(), a) && <AddTask activity={a} data={data} today={today} />}
        </Card>
      )}
      {assigned && a.stage === 0 && a.review !== 'rejected' && (
        <div style={{ margin: '14px 12px 0', padding: 16, borderRadius: R.pulse, background: C.amberBg, border: `1px solid ${C.amberBorder}`, font: font(500, 13, 1.45), color: C.amber }}>
          Proposal sent to UNIT Marketing &amp; Events. Tasks and a share kit are added once it is approved.
        </div>
      )}

      {a.needs.length > 0 && (
        <Card style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          <CardTitle>{assigned ? 'Open roles' : 'How you can help'}</CardTitle>
          {a.needs.map((n) => {
            const on = assigned ? Boolean(data.helpPosted[`${a.id}|${n}`]) : offered;
            return (
              <div key={n} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10, padding: 12, borderRadius: R.thumb, background: C.amberBg, border: `1px solid ${C.amberBorder}` }}>
                <span style={{ font: font(600, 14), color: C.amber }}>{n}</span>
                <Btn size="xs" style={{ border: `1px solid ${C.amber}`, background: on ? C.amber : C.white, color: on ? C.white : C.amber }} aria-pressed={on}
                  onClick={() => (assigned ? act.toggleHelpRole(a.id, n) : act.toggleOffer(a.id))}>
                  {assigned ? (on ? 'Posted ✓' : 'Post request') : on ? 'Offered ✓' : 'Offer help'}
                </Btn>
              </div>
            );
          })}
        </Card>
      )}

      <Card>
        <CardTitle right={assigned ? <button type="button" onClick={() => navigate({ name: 'collab', id: a.id })} style={{ border: 0, background: 'transparent', color: C.violet, font: font(600, 13), padding: '10px 0' }}>Coordinate ›</button> : undefined}>Team</CardTitle>
        <div style={{ marginTop: 6 }}>
          {a.team.map((m) => {
            const t = data.ambassadors[m.ambassadorId];
            if (!t) return null;
            return (
              <button key={m.ambassadorId} type="button" onClick={() => (t.id === data.me ? navigate({ name: 'me' }) : navigate({ name: 'profile', id: t.id }))}
                style={{ width: '100%', display: 'grid', gridTemplateColumns: '36px 1fr', gap: 12, alignItems: 'center', padding: '8px 0', border: 0, background: 'transparent', textAlign: 'left', minHeight: 52 }}>
                <Avatar person={t} />
                <span style={{ display: 'flex', flexDirection: 'column', gap: 1 }}><span style={{ font: font(600, 14), color: C.ink }}>{t.id === data.me ? `${t.name} (you)` : t.name}</span><span style={{ font: font(500, 11), color: C.grey }}>{m.role}</span></span>
              </button>
            );
          })}
        </div>
      </Card>

      <Card>
        <CardTitle>Updates</CardTitle>
        <ol style={{ listStyle: 'none', margin: '10px 0 0', padding: 0, display: 'flex', flexDirection: 'column' }}>
          {updates.map((u) => {
            const by = data.ambassadors[u.authorId];
            return (
              <li key={u.id} className="fade" style={{ display: 'grid', gridTemplateColumns: '14px 1fr', gap: 12, paddingBottom: 14 }}>
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                  <span style={{ width: 10, height: 10, borderRadius: '50%', background: u.health ? HEALTH_COLORS[u.health][0] : C.border, marginTop: 4 }} />
                  <span style={{ flex: 1, width: 1, background: C.subtle, marginTop: 4 }} />
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                  <span style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
                    <Mono size={10}>{`${u.authorId === data.me ? 'YOU' : by?.name.toUpperCase()} · ${ago(u.createdAt, nowMs)}${u.health ? ` · ${u.health.toUpperCase()}` : ''}`}</Mono>
                    {u.visibility === 'team' && <Tag label="TEAM ONLY" bg={C.divider} fg={C.grey} />}
                  </span>
                  <span style={{ font: font(500, 14, 1.4) }}>{u.text}{u.mediaCount ? ` (${u.mediaCount} attachment${u.mediaCount > 1 ? 's' : ''})` : ''}</span>
                </div>
              </li>
            );
          })}
          {!updates.length && <li style={{ font: font(400, 13), color: C.grey }}>No updates yet.</li>}
        </ol>
      </Card>
      {a.isHunt && (
        <p style={{ margin: '14px 24px 0', font: font(400, 12, 1.45), color: C.grey }}>Clue locations, coupon serials and claimant data are restricted to the organising team and never appear in UGAIN feeds.</p>
      )}
    </div>
  );
}

/** Lead organiser (or approver): assign a task to a team member. Server-enforced. */
function AddTask({ activity: a, data, today }: { activity: Activity; data: DemoData; today: string }) {
  const { act } = useApp();
  const [open, setOpen] = useState(false);
  const [title, setTitle] = useState('');
  const [owner, setOwner] = useState(data.me);
  const [due, setDue] = useState(today);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  if (!open) return <Btn variant="outline" full onClick={() => setOpen(true)} style={{ margin: '6px 0 12px' }}>+ Add task</Btn>;
  async function submit(e: FormEvent) {
    e.preventDefault();
    if (title.trim().length < 2) return setErr('Describe the task in a few words.');
    setBusy(true);
    const ok = await act.addTask(a.id, { title: title.trim(), ownerId: owner, due });
    setBusy(false);
    if (ok) {
      setTitle('');
      setOpen(false);
      setErr('');
    }
  }
  return (
    <form onSubmit={submit} noValidate style={{ display: 'flex', flexDirection: 'column', gap: 10, padding: '8px 0 14px' }}>
      <FieldLabel htmlFor="t-title">Task</FieldLabel>
      <input id="t-title" value={title} onChange={(e) => setTitle(e.target.value)} maxLength={120} placeholder="Print 50 posters" style={inputStyle} aria-invalid={Boolean(err)} />
      {err && <span role="alert" style={{ font: font(500, 12), color: C.red }}>{err}</span>}
      <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0,1.3fr) minmax(0,1fr)', gap: 8 }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          <FieldLabel htmlFor="t-owner">Owner</FieldLabel>
          <select id="t-owner" value={owner} onChange={(e) => setOwner(e.target.value)} style={inputStyle}>
            {a.team.map((m) => <option key={m.ambassadorId} value={m.ambassadorId}>{data.ambassadors[m.ambassadorId]?.name ?? 'Team member'}{m.ambassadorId === data.me ? ' (you)' : ''}</option>)}
          </select>
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          <FieldLabel htmlFor="t-due">Due</FieldLabel>
          <input id="t-due" type="date" min={today} value={due} onChange={(e) => setDue(e.target.value)} style={inputStyle} />
        </div>
      </div>
      <div style={{ display: 'flex', gap: 8 }}>
        <Btn type="submit" variant="primary" disabled={busy} aria-busy={busy}>{busy ? 'Adding…' : 'Add task'}</Btn>
        <Btn type="button" variant="outline" onClick={() => setOpen(false)}>Cancel</Btn>
      </div>
    </form>
  );
}

/** UNIT Marketing & Events approver: approve or decline a pending proposal (not their own). */
function ReviewPanel({ activity: a }: { activity: Activity }) {
  const { act } = useApp();
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const decide = async (d: 'approve' | 'reject') => {
    if (d === 'reject' && reason.trim().length < 3) return setErr('Give the organiser a short reason.');
    setErr('');
    setBusy(true);
    await act.decideProposal(a.id, d, reason.trim());
    setBusy(false);
  };
  return (
    <Card style={{ display: 'flex', flexDirection: 'column', gap: 10, border: `1px solid ${C.violet}` }}>
      <Eyebrow color={C.violet}>M+E review</Eyebrow>
      <span style={{ font: font(600, 15, 1.3) }}>This proposal is waiting for your decision.</span>
      <FieldLabel htmlFor="rv-reason">Note to the organiser (required to decline)</FieldLabel>
      <textarea id="rv-reason" value={reason} onChange={(e) => setReason(e.target.value)} rows={2} maxLength={300} style={textareaStyle} />
      {err && <span role="alert" style={{ font: font(500, 12), color: C.red }}>{err}</span>}
      <div style={{ display: 'flex', gap: 8 }}>
        <Btn variant="primary" disabled={busy} onClick={() => void decide('approve')}>Approve</Btn>
        <Btn variant="outline" disabled={busy} onClick={() => void decide('reject')}>Decline</Btn>
      </div>
    </Card>
  );
}
